/** Hero tab — crossfade `.hero-title__bg` layers on tab hover/focus + idle autoplay. */
const TAB = "[data-hero-bg]";
const AUTOPLAY_MS = 5000;
// Fast off the mark, easing out as it lands. Deliberately not a hard
// ease-out: the bar has to still look like it is moving when it reaches the
// end, or the advance reads as late.
const PROGRESS_EASE = "cubic-bezier(0.25, 0.75, 0.3, 1)";

function heroTab(section) {
  const bg = section.querySelector(".hero-title__bg");
  const layers = bg ? [...bg.querySelectorAll(".hero-title__bg-layer")] : [];
  const list = section.querySelector(".hero-tab__list");
  if (!bg || layers.length < 2 || !list) return;

  const tabs = [...list.querySelectorAll(TAB)];
  if (!tabs.length) return;

  const raw = getComputedStyle(bg).getPropertyValue("--hero-bg-fade-duration");
  const n = Number.parseFloat(raw);
  const fade = Number.isNaN(n) ? 500 : raw.trim().endsWith("ms") ? n : n * 1000;
  let idx = -1;
  let tabIndex = 0;
  let timer;
  let leaveTimer;
  let autoplayTimer;
  let paused = false;
  let started = false;
  let inView = false;
  const reduceMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;

  const resolve = (url) => {
    try {
      return new URL(url, location.href).href;
    } catch {
      return url;
    }
  };

  const same = (img, url) => {
    const src = img.getAttribute("src") || "";
    const resolved = resolve(url);
    return (
      src === url ||
      img.src === resolved ||
      img.src.endsWith(url.replace(/^\.\//, ""))
    );
  };

  const load = (img, url) =>
    new Promise((done) => {
      if (same(img, url) && img.complete && img.naturalWidth) {
        done();
        return;
      }
      const finish = () => {
        img.onload = img.onerror = null;
        done();
      };
      img.onload = img.onerror = finish;
      img.src = resolve(url);
      if (img.complete) finish();
    });

  const bars = tabs.map((t) => t.querySelector(".hero-tab__progress"));
  let barAnim;

  /** Clear every fill; the active one is restarted by runProgress. */
  const resetProgress = () => {
    if (barAnim) {
      barAnim.cancel();
      barAnim = undefined;
    }
    bars.forEach(
      (bar) => bar && bar.getAnimations().forEach((a) => a.cancel()),
    );
  };

  /** Run the fill on one tab for exactly as long as it stays on screen. */
  const runProgress = (tab) => {
    resetProgress();
    const bar = bars[tabs.indexOf(tab)];
    if (!bar || reduceMotion) return;
    barAnim = bar.animate(
      [{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }],
      { duration: AUTOPLAY_MS, easing: PROGRESS_EASE, fill: "forwards" },
    );
  };

  const clear = (layer) => layer.classList.remove("is-active", "is-leaving");
  const active = () => (idx >= 0 ? layers[idx] : null);
  const next = () => (idx === -1 ? layers[0] : layers[idx ^ 1]);
  const setTabs = (tab) =>
    tabs.forEach((t) => t.classList.toggle("is-active", t === tab));

  const reveal = (layer, activeTab) => {
    clear(layer);
    layer.classList.add("is-active");
    idx = layers.indexOf(layer);
    bg.classList.add("is-visible");
    setTabs(activeTab);
    if (canAutoplay() && started) {
      // Arm the advance clock first: scheduleAutoplay clears any running fill
      // on its way through stopAutoplay, so starting the bar before it would
      // cancel the bar we just started.
      scheduleAutoplay();
      runProgress(activeTab);
    } else {
      resetProgress();
    }
  };

  const show = async (tab) => {
    const url = tab.dataset.heroBg;
    if (!url) return;
    clearTimeout(timer);
    clearTimeout(leaveTimer);

    const cur = active();
    const existing = layers.find((layer) => {
      const img = layer.querySelector("img");
      return img && same(img, url);
    });

    if (existing) {
      if (cur && cur !== existing) {
        cur.classList.remove("is-active");
        cur.classList.add("is-leaving");
        leaveTimer = setTimeout(() => clear(cur), fade);
      }
      reveal(existing, tab);
      return;
    }

    const layer = next();
    await load(layer.querySelector("img"), url);

    if (cur) {
      cur.classList.remove("is-active");
      cur.classList.add("is-leaving");
      leaveTimer = setTimeout(() => clear(cur), fade);
    }

    reveal(layer, tab);
  };

  const stopAutoplay = () => {
    clearTimeout(autoplayTimer);
    autoplayTimer = undefined;
    resetProgress();
  };

  const canAutoplay = () =>
    !paused && !reduceMotion && inView && !document.hidden;

  const advance = () => {
    if (!canAutoplay()) return;
    tabIndex = (tabIndex + 1) % tabs.length;
    show(tabs[tabIndex]);
  };

  const scheduleAutoplay = () => {
    stopAutoplay();
    if (!canAutoplay()) return;
    autoplayTimer = setTimeout(advance, AUTOPLAY_MS);
  };

  /** Activate the first tab as soon as the hero is in view, then keep cycling. */
  const kickoff = () => {
    stopAutoplay();
    if (!canAutoplay() || started) return;
    started = true;
    show(tabs[tabIndex]);
  };

  const pauseAutoplay = () => {
    paused = true;
    stopAutoplay();
    resetProgress();
  };

  const resumeAutoplay = () => {
    paused = false;
    if (!started) kickoff();
    else {
      scheduleAutoplay();
      if (canAutoplay()) runProgress(tabs[tabIndex]);
    }
  };

  const onTab = (e) => {
    const tab = e.target.closest(TAB);
    if (!tab || !list.contains(tab)) return;
    pauseAutoplay();
    tabIndex = tabs.indexOf(tab);
    started = true;
    show(tab);
  };

  const onLeave = () => {
    resumeAutoplay();
  };

  list.addEventListener("pointerover", onTab);
  list.addEventListener("focusin", onTab);
  list.addEventListener("pointerleave", onLeave);
  list.addEventListener("focusout", (e) => {
    if (!list.contains(e.relatedTarget)) onLeave();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stopAutoplay();
    else if (!paused) {
      if (!started) kickoff();
      else scheduleAutoplay();
    }
  });

  // Warm the first tab's background straight away so the opening fill is not
  // waiting on a network round trip.
  const firstUrl = tabs[0] && tabs[0].dataset.heroBg;
  if (firstUrl) load(layers[0].querySelector("img"), firstUrl);

  const io = new IntersectionObserver(
    ([entry]) => {
      inView = entry.isIntersecting;
      if (inView) {
        if (!paused && !started) kickoff();
        else if (!paused && started) scheduleAutoplay();
      } else {
        stopAutoplay();
      }
    },
    { threshold: 0.25 },
  );
  io.observe(section);
}

function init() {
  document.querySelectorAll(".section--hero-title").forEach(heroTab);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
